import { computed, ref } from 'vue'

import { isInstalledApp } from '@/utils/installedApp'

export { isInstalledApp }

const DISMISS_KEY = 'escribonauta_pwa_install_dismissed'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

export function isIosSafari(): boolean {
  const ua = window.navigator.userAgent
  const isIos = /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isWebkit = /WebKit/.test(ua)
  const isOtherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Firefox|Edg/.test(ua)
  return isIos && isWebkit && !isOtherBrowser
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const dismissed = ref(readDismissed())

// Capture early — beforeinstallprompt can fire before any Vue component mounts.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt.value = event as BeforeInstallPromptEvent
  })
}

/**
 * Lightweight install prompt: Android/Chrome via beforeinstallprompt;
 * iOS Safari tip when not already installed as standalone.
 */
export function usePwaInstall() {
  const isInstalled = computed(() => isInstalledApp())
  const canPromptInstall = computed(
    () => !!deferredPrompt.value && !dismissed.value && !isInstalled.value,
  )
  const showIosTip = computed(
    () => isIosSafari() && !isInstalled.value && !dismissed.value && !deferredPrompt.value,
  )
  const visible = computed(() => canPromptInstall.value || showIosTip.value)
  const installPromptReady = computed(() => !!deferredPrompt.value && !isInstalled.value)
  const iosInstallInstructions = computed(
    () => isIosSafari() && !isInstalled.value && !deferredPrompt.value,
  )

  function dismiss() {
    dismissed.value = true
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // Ignore storage errors
    }
  }

  async function promptInstall(): Promise<boolean> {
    const promptEvent = deferredPrompt.value
    if (!promptEvent) return false

    await promptEvent.prompt()
    const choice = await promptEvent.userChoice
    deferredPrompt.value = null

    if (choice.outcome === 'accepted') {
      dismiss()
      return true
    }

    return false
  }

  return {
    visible,
    canPromptInstall,
    showIosTip,
    isInstalled,
    installPromptReady,
    iosInstallInstructions,
    promptInstall,
    dismiss,
  }
}
